import graphqlClient from './graphqlClient';

export interface MapConfig {
  include_kpis: {
    enabled: boolean;
    total_sites: boolean;
    total_neighbourhood_incidents: boolean;
    sites_in_high_crime_area: boolean;
  };
  neighbourhood_stores: {
    enabled: boolean;
    display_mode: 'show_as_logos' | 'anonymise_stores';
    neighbourhood_channel: 'show_all' | 'show_same';
    neighbourhood_store_type: 'all' | 'customer' | 'non_customer';
    installed_solutions?: string[];
    whitespace?: string[];
  };
  include_filters: {
    enabled: boolean;
    customer_type: boolean;
    state: boolean;
    city: boolean;
    recommended_solution: boolean;
    installed_solution: boolean;
    crime_index: boolean;
    weather_condition: boolean;
    shopping_complex: boolean;
    cart_ordinance: boolean;
  };
}

export const DEFAULT_MAP_CONFIG: MapConfig = {
  include_kpis: {
    enabled: true,
    total_sites: true,
    total_neighbourhood_incidents: true,
    sites_in_high_crime_area: true,
  },
  neighbourhood_stores: {
    enabled: false,
    display_mode: 'show_as_logos',
    neighbourhood_channel: 'show_all',
    neighbourhood_store_type: 'all',
    installed_solutions: [],
    whitespace: [],
  },
  include_filters: {
    enabled: true,
    customer_type: true,
    state: true,
    city: true,
    recommended_solution: true,
    installed_solution: true,
    crime_index: true,
    weather_condition: true,
    shopping_complex: true,
    cart_ordinance: true,
  },
};

export interface ExportedOpportunity {
  id: string;
  token: string | null;
  opportunity_id: string | null;
  created_by: string | null;
  is_active: boolean;
  created_at: string;
  poi_ids: any;
  allowed_emails: string[] | null;
  allowed_domains: string[] | null;
  map_config: MapConfig | null;
}

const fetchPoiRows = async (poiIds: string[]): Promise<any[]> => {
  if (!poiIds || poiIds.length === 0) return [];

  const query = `
    query GetPoisForExport($poiIds: [String!]!) {
      udm_poi(where: { id: { _in: $poiIds } }) {
        id
        site
        brand_name
        address
        latitude
        longitude
        channel
        customer_id
        zipcode
        purchek_install_date
        containment_install_date
        cart_manager_install_date
        subscription_start_date
        paired_wheel_install_date
        rank
        cart_manager_rank
        purchek_rank
        containment_rank
        lifetime_value
        geo {
          total_crime_incidents
          city
          state
          zipcode
          county
          crime_rate
          no_of_hot_days
          no_of_cold_days
          penalty_for_cart_owner
          law_summary
          law_source
        }
        nearby_penetration {
          penetration
          penetration_purchek
          penetration_containment
          penetration_paired_wheel
          purchek_customer_count
          containment_customer_count
          plaza_cm_customer_count
          shoplifting_amount_usd
          shoplifting_incident_count
        }
        video {
          total_crime_amount_usd
          incident_count
        }
        wheel {
          single_wheel_replacement_rate
          service_frequency
        }
      }
    }
  `;

  const response = await graphqlClient.request<any>(query, { poiIds });
  return response.udm_poi || [];
};

export const fetchExportedOpportunityIds = async (): Promise<Set<string>> => {
  try {
    const query = `
      query GetExportedOpportunityIds {
        app_exported_opportunity(where: { is_active: { _eq: true } }) {
          opportunity_id
        }
      }
    `;
    const response = await graphqlClient.request<any>(query);
    const records = response.app_exported_opportunity || [];
    return new Set(records.map((r: any) => r.opportunity_id).filter(Boolean));
  } catch (error: any) {
    console.error('[ExportedOpportunities] Fetch IDs error:', error);
    return new Set();
  }
};

export const fetchExportedOpportunity = async (
  opportunityId: string
): Promise<ExportedOpportunity | null> => {
  try {
    const query = `
      query GetExportedOpportunity($opportunityId: String!) {
        app_exported_opportunity(
          where: { opportunity_id: { _eq: $opportunityId }, is_active: { _eq: true } },
          order_by: { created_at: desc },
          limit: 1
        ) {
          id
          token
          opportunity_id
          created_by
          is_active
          created_at
          poi_ids
          allowed_emails
          allowed_domains
          map_config
        }
      }
    `;
    const response = await graphqlClient.request<any>(query, { opportunityId });
    const records = response.app_exported_opportunity || [];
    return records.length > 0 ? records[0] : null;
  } catch (error: any) {
    console.error('[ExportedOpportunities] Fetch error:', error);
    return null;
  }
};

export const updateExportedOpportunityMapConfig = async (
  opportunityId: string,
  mapConfig: MapConfig
): Promise<{ success: boolean; message: string }> => {
  try {
    const mutation = `
      mutation UpdateExportedOpportunityMapConfig(
        $opportunityId: String!,
        $map_config: jsonb
      ) {
        update_app_exported_opportunity(
          where: { opportunity_id: { _eq: $opportunityId }, is_active: { _eq: true } },
          _set: { map_config: $map_config }
        ) {
          affected_rows
        }
      }
    `;

    await graphqlClient.request<any>(mutation, {
      opportunityId,
      map_config: mapConfig,
    });

    return { success: true, message: 'Map configuration saved successfully' };
  } catch (error: any) {
    console.error('[ExportedOpportunities] Update map config error:', error);
    return {
      success: false,
      message: error?.response?.errors?.[0]?.message || error.message || 'Failed to update map configuration',
    };
  }
};

export const createExportedOpportunity = async (
  opportunityId: string,
  storePoiIds: string[],
  mapConfig: MapConfig | null = null
): Promise<{ success: boolean; message: string; data?: ExportedOpportunity }> => {
  try {
    const poiRows = await fetchPoiRows(storePoiIds);

    const id = crypto.randomUUID();
    const token = crypto.randomUUID();

    const mutation = `
      mutation CreateExportedOpportunity(
        $id: uuid!,
        $token: String,
        $opportunity_id: String,
        $poi_ids: jsonb,
        $map_config: jsonb
      ) {
        insert_app_exported_opportunity_one(object: {
          id: $id,
          token: $token,
          opportunity_id: $opportunity_id,
          is_active: true,
          poi_ids: $poi_ids,
          map_config: $map_config
        }) {
          id
          token
          opportunity_id
          created_by
          is_active
          created_at
          poi_ids
          allowed_emails
          allowed_domains
          map_config
        }
      }
    `;

    const response = await graphqlClient.request<any>(mutation, {
      id,
      token,
      opportunity_id: opportunityId,
      poi_ids: poiRows,
      map_config: mapConfig,
    });

    return {
      success: true,
      message: 'Exported opportunity created successfully',
      data: response.insert_app_exported_opportunity_one,
    };
  } catch (error: any) {
    console.error('[ExportedOpportunities] Create error:', error);
    return {
      success: false,
      message: error?.response?.errors?.[0]?.message || error.message || 'Failed to create exported opportunity',
    };
  }
};

export const updateExportedOpportunitySharing = async (
  opportunityId: string,
  allowedEmails: string[],
  allowedDomains: string[]
): Promise<{ success: boolean; message: string }> => {
  try {
    const mutation = `
      mutation UpdateExportedOpportunitySharing(
        $opportunityId: String!,
        $allowed_emails: [String!],
        $allowed_domains: [String!]
      ) {
        update_app_exported_opportunity(
          where: { opportunity_id: { _eq: $opportunityId }, is_active: { _eq: true } },
          _set: { allowed_emails: $allowed_emails, allowed_domains: $allowed_domains }
        ) {
          affected_rows
        }
      }
    `;

    await graphqlClient.request<any>(mutation, {
      opportunityId,
      allowed_emails: allowedEmails,
      allowed_domains: allowedDomains,
    });

    return { success: true, message: 'Sharing settings updated successfully' };
  } catch (error: any) {
    console.error('[ExportedOpportunities] Update sharing error:', error);
    return {
      success: false,
      message: error?.response?.errors?.[0]?.message || error.message || 'Failed to update sharing settings',
    };
  }
};
